import { useCallback } from 'react';

import { getBooleanFF } from '@atlaskit/platform-feature-flags';

import { fg } from '@confluence/feature-gating';
import { isContentTypeEnabledInCurrentEnv } from '@confluence/content-types-utils/entry-points/isContentTypeEnabledInCurrentEnv';

import featureGates from './whiteboard-feature-gates.json';

export type WhiteboardFeatures = keyof typeof featureGates;

// These flags are no longer used in confluence and can be removed from this array entirely after we confirm that have been removed from the whiteboards side too
const ARCHIVED_FEATURE_FLAGS = [
	'headerV1',
	'headerV2',
	'whiteboardArchive',
	'removeWhiteboardsHome',
	'removeWhiteboardsHomeInSideNav',
	'whiteboardCreation',
	'whiteboardsEnabled',
	'whiteboardBifrost',
	'whiteboardVersionHistory',
] as const;
type ArchivedFeatureFlags = (typeof ARCHIVED_FEATURE_FLAGS)[number];
type ActiveFeatureGates = keyof typeof featureGates;

export const useIsWhiteboardFeatureEnabled = () => {
	const isWhiteboardsEnabledInEnv = isContentTypeEnabledInCurrentEnv('whiteboard');

	const isWhiteboardFeatureEnabled = useCallback(
		(featureName: ArchivedFeatureFlags | ActiveFeatureGates) => {
			if (featureName === 'whiteboardsEnabled') {
				return isWhiteboardsEnabledInEnv;
			}

			// If the FF has been cleaned up on our end just return true
			if (ARCHIVED_FEATURE_FLAGS.some((feature) => feature === featureName)) {
				return true;
			}

			if (featureName in featureGates) {
				const activeFeatureGate = featureName as ActiveFeatureGates;
				const featureGateKey = featureGates[activeFeatureGate];
				return fg(featureGateKey);
			}

			return false;
		},
		[isWhiteboardsEnabledInEnv],
	);

	const isWhiteboardCreationDropdownEnabled = useCallback((): boolean => {
		return (
			isWhiteboardFeatureEnabled('whiteboardsEnabled') &&
			!isWhiteboardFeatureEnabled('whiteboardOptOut')
		);
	}, [isWhiteboardFeatureEnabled]);

	const isWhiteboardGAOnboardingEnabled = useCallback(() => {
		return (
			isWhiteboardFeatureEnabled('whiteboardsEnabled') &&
			!isWhiteboardFeatureEnabled('whiteboardOptOut')
		);
	}, [isWhiteboardFeatureEnabled]);

	return {
		platformFeatureFlagResolver: getBooleanFF,
		isWhiteboardFeatureEnabled,
		isWhiteboardCreationDropdownEnabled,
		isWhiteboardGAOnboardingEnabled,
	};
};
